import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageSquare, Heart, Plus, CheckCircle2, Loader2, Folder, Search, Pin, Megaphone } from "lucide-react";
import { toast } from "sonner";
import ForumAttach from "@/components/ForumAttach";

export default function Forum() {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [topics, setTopics] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [nt, setNt] = useState({ category_id: "", title: "", body: "", attachments: [] });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [c, t] = await Promise.all([api.get("/forum/categories"), api.get("/forum/topics")]);
      setCategories(c.data);
      setTopics(t.data);
    } catch (e) {
      toast.error("Nie udało się załadować forum");
    }
  };

  useEffect(() => { load(); }, []);

  const createTopic = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/forum/topics", nt);
      toast.success("Wątek utworzony");
      setOpen(false);
      setNt({ category_id: "", title: "", body: "", attachments: [] });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleLike = async (id) => {
    try {
      const { data } = await api.post(`/forum/topics/${id}/like`);
      setTopics((prev) => prev.map((t) => (t.id === id ? { ...t, like_count: data.like_count, liked: data.liked } : t)));
    } catch {}
  };

  const togglePin = async (id) => {
    try {
      await api.post(`/forum/topics/${id}/pin`);
      load();
    } catch {}
  };

  const catName = (id) => categories.find((c) => c.id === id)?.name || "Ogólne";
  const q = search.trim().toLowerCase();
  const filtered = (activeCat === "all" ? topics : topics.filter((t) => t.category_id === activeCat))
    .filter((t) => !q || (t.title || "").toLowerCase().includes(q) || (t.body || "").toLowerCase().includes(q));

  return (
    <div>
      <PageHeader label="Strefa członkowska" title="Forum dyskusyjne" subtitle={`Witaj, ${user?.name}. Rozmawiaj z operatorami i ekspertami branży.`}>
        <div className="mt-8">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="lg" className="gap-2 bg-ztk-blue hover:bg-ztk-glow" data-testid="forum-create-topic-button">
                <Plus className="h-4 w-4" /> Nowy wątek
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display">Utwórz nowy wątek</DialogTitle></DialogHeader>
              <form onSubmit={createTopic} className="space-y-4">
                <div>
                  <Label>Kategoria</Label>
                  <Select value={nt.category_id} onValueChange={(v) => setNt({ ...nt, category_id: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="forum-topic-category"><SelectValue placeholder="Wybierz kategorię" /></SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tytuł</Label>
                  <Input required value={nt.title} onChange={(e) => setNt({ ...nt, title: e.target.value })} data-testid="forum-topic-title" className="mt-1.5" />
                </div>
                <div>
                  <Label>Treść</Label>
                  <Textarea required rows={5} value={nt.body} onChange={(e) => setNt({ ...nt, body: e.target.value })} data-testid="forum-topic-body" className="mt-1.5" />
                </div>
                <ForumAttach value={nt.attachments} onChange={(v) => setNt({ ...nt, attachments: v })} />
                <DialogFooter>
                  <Button type="submit" disabled={saving || !nt.category_id} className="bg-ztk-blue" data-testid="forum-topic-submit">
                    {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Opublikuj
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </PageHeader>

      <div className="mx-auto grid max-w-7xl gap-8 px-4 py-16 sm:px-6 lg:grid-cols-4 lg:px-8">
        <aside className="lg:col-span-1">
          <p className="label-mono mb-3 text-muted-foreground">Kategorie</p>
          <nav className="space-y-1">
            <button
              onClick={() => setActiveCat("all")}
              data-testid="forum-cat-all"
              className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors ${activeCat === "all" ? "bg-ztk-blue text-white" : "hover:bg-secondary"}`}
            >
              <Folder className="h-4 w-4" /> Wszystkie <span className="ml-auto text-xs opacity-70">{topics.length}</span>
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                onClick={() => setActiveCat(c.id)}
                data-testid={`forum-cat-${c.id}`}
                className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors ${activeCat === c.id ? "bg-ztk-blue text-white" : "hover:bg-secondary"}`}
              >
                <Folder className="h-4 w-4 shrink-0" />
                <span className="truncate">{c.name}</span>
                <span className="ml-auto text-xs opacity-70">{c.topic_count}</span>
              </button>
            ))}
          </nav>
        </aside>

        <div className="lg:col-span-3">
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Szukaj wątków po tytule lub treści..."
              className="pl-9"
              data-testid="forum-search-input"
            />
          </div>
          {filtered.length === 0 && (
            <div className="rounded-2xl border border-dashed border-border py-16 text-center text-muted-foreground" data-testid="forum-empty">
              <MessageSquare className="mx-auto mb-3 h-10 w-10 opacity-40" />
              {q ? "Brak wątków pasujących do wyszukiwania." : "Brak wątków. Rozpocznij pierwszą dyskusję!"}
            </div>
          )}
          <div className="space-y-4">
            {filtered.map((t) => (
              <div key={t.id} data-testid={`forum-topic-${t.id}`} className={`rounded-2xl border bg-card p-6 transition-all hover:shadow-lg ${t.pinned ? "border-ztk-yellow/60 ring-1 ring-ztk-yellow/30" : "border-border hover:border-ztk-blue/40"}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      {t.pinned && <Badge className="gap-1 bg-ztk-yellow text-[10px] text-ztk-navydark hover:bg-ztk-yellow"><Pin className="h-3 w-3" /> Przypięty</Badge>}
                      {t.author_role === "admin" && <Badge className="gap-1 bg-ztk-blue text-[10px] hover:bg-ztk-blue"><Megaphone className="h-3 w-3" /> Ogłoszenie administratora</Badge>}
                      <Badge variant="secondary" className="text-[10px]">{catName(t.category_id)}</Badge>
                      {t.solved && <Badge className="gap-1 bg-emerald-500 text-[10px] hover:bg-emerald-500"><CheckCircle2 className="h-3 w-3" /> Rozwiązany</Badge>}
                    </div>
                    <Link to={`/forum/${t.id}`} className="font-display text-lg font-semibold text-ztk-navy hover:text-ztk-blue dark:text-white">
                      {t.title}
                    </Link>
                    <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{t.body}</p>
                    <p className="mt-3 text-xs text-muted-foreground">
                      {t.author_name} · {new Date(t.created_at).toLocaleDateString("pl-PL")}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-col items-center gap-3">
                    {user?.role === "admin" && (
                      <button onClick={() => togglePin(t.id)} data-testid={`forum-pin-button-${t.id}`} title={t.pinned ? "Odepnij" : "Przypnij"}
                        className={`transition-colors ${t.pinned ? "text-ztk-yellow" : "text-muted-foreground hover:text-ztk-yellow"}`}>
                        <Pin className={`h-5 w-5 ${t.pinned ? "fill-ztk-yellow" : ""}`} />
                      </button>
                    )}
                    <button onClick={() => toggleLike(t.id)} data-testid={`forum-like-button-${t.id}`} className="flex flex-col items-center text-xs text-muted-foreground transition-colors hover:text-ztk-red">
                      <Heart className={`h-5 w-5 ${t.liked ? "fill-ztk-red text-ztk-red" : ""}`} />
                      {t.like_count}
                    </button>
                    <div className="flex flex-col items-center text-xs text-muted-foreground">
                      <MessageSquare className="h-5 w-5" />
                      {t.reply_count}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
